// src/views/RecurringInvoicesView.js  recinv-list-9d4e7b30
// READ-ONLY list of recurring-invoice schedules (Seam 2a). Follows InvoicesView's
// patterns: a config-driven COLUMNS array drives the desktop table; the mobile card
// view is hand-built; the collection is subscribed live and filtered/sorted client-
// side. No writes, no batch, no column picker (deferred — the columnPrefs whitelist
// already supports it at zero auth cost; see BACKLOG).
import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Search, XCircle, AlertTriangle, CheckCircle, ArrowUp, ArrowDown, Repeat, Plus } from 'lucide-react';
import { formatDate, toJsDate } from '../utils/formatters';
import RecurringScheduleStatusBadge from '../components/common/RecurringScheduleStatusBadge';
import { describeRecurrence, scheduleHealth, needsAttention } from '../utils/recurringInvoices';
import { canSeeAllCustomers } from '../utils/permissions'; // s9office-6e2a9d53

const RUN_TYPE_LABELS = { auto_send: 'Auto-send', draft: 'Draft' };
const NEVER_ENDS_YEAR = 2999;

// A schedule's nextRunDate is parked at the 2999 sentinel once ended, and may be
// missing/unparseable on a malformed doc — show a dash rather than "12/31/2999" or
// crashing.
const fmtRunDate = (value) => {
    const d = toJsDate(value);
    if (!d || d.getFullYear() >= NEVER_ENDS_YEAR) return '—';
    return formatDate(d);
};

// recinv-list-health -- one amber/red AlertTriangle (hover = reasons) or a green
// check, reusing the QB cell's colour vocabulary: amber = attention, red = failure,
// green = healthy.
const renderHealth = (schedule) => {
    const h = scheduleHealth(schedule);
    if (h.level === 'broken') return <span title={h.brokenReason}><AlertTriangle size={16} className="text-red-500" /></span>;
    if (h.level === 'attention') return <span title={h.attention.join(' ')}><AlertTriangle size={16} className="text-amber-500" /></span>;
    return <span title="Healthy"><CheckCircle size={16} className="text-green-500" /></span>;
};

// colcfg-recinv -- header, body, and empty-state colSpan all read from this ONE
// array (same discipline as InvoicesView's COLUMNS).
const COLUMNS = [
    { id: 'name', label: 'Name', sortKey: 'name',
        thClass: 'px-6 py-3', tdClass: 'px-6 py-4 font-medium text-gray-900',
        renderCell: (s) => s.name || s.title || '—' },
    { id: 'customer', label: 'Customer', sortKey: 'customerName',
        thClass: 'px-6 py-3', tdClass: 'px-6 py-4',
        renderCell: (s) => s.customerDetails?.name || s.customerName || '—' },
    { id: 'repeats', label: 'Repeats', sortKey: null,
        thClass: 'px-6 py-3', tdClass: 'px-6 py-4',
        renderCell: (s) => describeRecurrence(s.recurrence) },
    { id: 'nextRun', label: 'Next Run', sortKey: 'nextRunSort',
        thClass: 'px-6 py-3', tdClass: 'px-6 py-4',
        renderCell: (s) => fmtRunDate(s.nextRunDate) },
    { id: 'runType', label: 'Run Type', sortKey: 'runType',
        thClass: 'px-6 py-3', tdClass: 'px-6 py-4',
        renderCell: (s) => RUN_TYPE_LABELS[s.runType] || 'Draft' },
    { id: 'status', label: 'Status', sortKey: 'status',
        thClass: 'px-6 py-3 text-center', tdClass: 'px-6 py-4 text-center',
        renderCell: (s) => <RecurringScheduleStatusBadge status={s.status} /> },
    { id: 'health', label: 'Health', sortKey: null,
        thClass: 'px-6 py-3 text-center', tdClass: 'px-6 py-4 text-center',
        renderCell: (s) => renderHealth(s) },
];

const RecurringInvoicesView = ({ currentUserProfile, onScheduleSelect, onNew }) => {
    const [schedules, setSchedules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilters, setStatusFilters] = useState(new Set());
    // Own boolean, NOT folded into statusFilters, deliberately NOT persisted — mirrors
    // qbAttentionOnly (InvoicesView.js:170) so a reload never leaves a silent filter on.
    const [attentionOnly, setAttentionOnly] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'nextRunSort', direction: 'ascending' });

    const toggleStatus = (s) => setStatusFilters(prev => {
        const next = new Set(prev);
        if (next.has(s)) next.delete(s); else next.add(s);
        return next;
    });

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const path = `organizations/${currentUserProfile.organizationId}/recurringInvoices`;
        // NO orderBy — DELIBERATE. orderBy('nextRunDate') would SILENTLY EXCLUDE any
        // schedule whose nextRunDate is missing or unparseable, with no error — a
        // schedule that quietly stops appearing is the exact failure this feature
        // exists to catch. Subscribe to the whole collection and sort client-side on
        // nextRunSort; the Infinity fallback sorts malformed docs to the bottom, where
        // they stay VISIBLE (and fmtRunDate shows them as '—').
        const unsub = onSnapshot(collection(db, path), (snap) => {
            const all = snap.docs.map(d => {
                const data = d.data();
                const nr = toJsDate(data.nextRunDate);
                return { id: d.id, ...data, nextRunSort: nr ? nr.getTime() : Infinity };
            });
            // Row-visibility SECURITY LOGIC — copied verbatim from InvoicesView.js:285
            // (isHighLevel inline; NOT refactored this seam — see BACKLOG).
            // s9office-6e2a9d53 — S9b (founder decision 2026-10-04): Office Staff see ALL schedules,
            // like invoices and estimates. canSeeAllCustomers also fixes the plain 'Owner' /
            // 'System Admin' spellings the inline list left out. Display scoping only — every staff
            // member could already READ every schedule (firestore.rules recurringInvoices).
            const seesAll = canSeeAllCustomers(currentUserProfile);
            setSchedules(all.filter(s => seesAll ? true : s.createdBy === currentUserProfile.id));
            setIsLoading(false);
        }, (err) => { console.error('Error fetching recurring invoices:', err); setIsLoading(false); });
        return () => unsub();
    }, [currentUserProfile]);

    const sorted = useMemo(() => {
        let out = [...schedules];
        if (statusFilters.size > 0) out = out.filter(s => statusFilters.has(s.status));
        if (attentionOnly) out = out.filter(needsAttention);
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            out = out.filter(s =>
                (s.name || '').toLowerCase().includes(q) ||
                (s.customerDetails?.name || s.customerName || '').toLowerCase().includes(q));
        }
        if (sortConfig.key) {
            out.sort((a, b) => {
                let av = a[sortConfig.key], bv = b[sortConfig.key];
                if (sortConfig.key === 'customerName') { av = a.customerDetails?.name || a.customerName || ''; bv = b.customerDetails?.name || b.customerName || ''; }
                if (typeof av === 'string' && typeof bv === 'string') {
                    if (av.toLowerCase() < bv.toLowerCase()) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (av.toLowerCase() > bv.toLowerCase()) return sortConfig.direction === 'ascending' ? 1 : -1;
                } else {
                    if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
                    if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return out;
    }, [schedules, statusFilters, attentionOnly, searchTerm, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <span className="ml-1 inline-block w-4"></span>;
        return sortConfig.direction === 'ascending'
            ? <ArrowUp className="inline ml-1 h-4 w-4 text-blue-600" />
            : <ArrowDown className="inline ml-1 h-4 w-4 text-blue-600" />;
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500 animate-pulse">Loading recurring invoices...</div>;

    return (
        <div className="p-3 sm:p-6 md:p-10 pb-20">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2"><Repeat size={26} /> Recurring Invoices</h1>
                    <span className="text-xs text-gray-500 mt-2 inline-block">{sorted.length} schedule(s)</span>
                </div>
                <div className="w-full sm:w-auto flex flex-wrap gap-2 items-center">
                    <div className="relative w-full sm:w-56">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input type="text" placeholder="Search schedules..." value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                    </div>
                    {onNew && ( // recinvform-list-newbtn-9f3c2a71
                        <button onClick={onNew} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
                            <Plus size={16} /> New Recurring Invoice
                        </button>
                    )}
                </div>
            </div>

            {/* --- FILTER BAR --- */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
                <div className="flex items-center gap-1">
                    {['active', 'paused', 'ended'].map(s => (
                        <button key={s} onClick={() => toggleStatus(s)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize
                                ${statusFilters.has(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            {s}
                        </button>
                    ))}
                    {statusFilters.size > 0 && (
                        <button onClick={() => setStatusFilters(new Set())} className="text-gray-400 hover:text-red-500 ml-1"><XCircle size={14} /></button>
                    )}
                </div>
                <div className="flex items-center border-l pl-3 ml-1">
                    <button onClick={() => setAttentionOnly(v => !v)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                            ${attentionOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        <AlertTriangle size={13} /> Needs attention
                    </button>
                </div>
            </div>

            {/* --- DESKTOP TABLE --- */}
            <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                        <tr>
                            {COLUMNS.map(col => (
                                col.sortKey
                                    ? <th key={col.id} className={`${col.thClass} cursor-pointer hover:bg-gray-100`} onClick={() => requestSort(col.sortKey)}>{col.label}{getSortIcon(col.sortKey)}</th>
                                    : <th key={col.id} className={col.thClass}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(s => (
                            <tr key={s.id} onClick={() => onScheduleSelect(s)}
                                className="cursor-pointer transition-colors bg-white hover:bg-blue-50">
                                {COLUMNS.map(col => (
                                    <td key={col.id} className={col.tdClass}>{col.renderCell(s)}</td>
                                ))}
                            </tr>
                        ))}
                        {sorted.length === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="text-center py-10 text-gray-500">No recurring invoices match the current filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE CARDS --- */}
            <div className="md:hidden space-y-3">
                {sorted.map(s => {
                    const h = scheduleHealth(s);
                    return (
                        <div key={s.id} onClick={() => onScheduleSelect(s)}
                            className="p-4 rounded-xl shadow-sm border active:scale-[0.99] transition-all cursor-pointer bg-white border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 leading-tight">{s.name || s.title || 'Untitled schedule'}</h3>
                                    <p className="text-sm text-gray-500">{s.customerDetails?.name || s.customerName || '—'}</p>
                                </div>
                                <RecurringScheduleStatusBadge status={s.status} />
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><p className="text-xs text-gray-500">Repeats</p><p className="font-medium text-gray-700">{describeRecurrence(s.recurrence)}</p></div>
                                <div><p className="text-xs text-gray-500">Next run</p><p className="font-medium text-gray-700">{fmtRunDate(s.nextRunDate)}</p></div>
                            </div>
                            {h.level !== 'healthy' && (
                                <p className={`text-xs mt-2 ${h.level === 'broken' ? 'text-red-600' : 'text-amber-600'}`}>
                                    {h.level === 'broken' ? h.brokenReason : h.attention.join(' ')}
                                </p>
                            )}
                        </div>
                    );
                })}
                {sorted.length === 0 && (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500">No recurring invoices found.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecurringInvoicesView;
